import React from 'react';
import { Box } from '@apia/theme';
import { RequiredMark } from '@apia/components';
import { formatMessage } from '@apia/util';
import type { TFilter } from './types';

export interface IFilterLabelProps {
  state: TFilter;
  /**
   * Si true, no renderiza el texto del label (solo la estructura).
   * Útil para casos en que el control renderiza su propio label.
   */
  avoidLabel?: boolean;
  /**
   * Forzar un texto en lugar de state.title
   */
  labelOverride?: string;
  /**
   * Indica que el label corresponde a un filtro fecha (para usar
   * LBL_DATE_FILTER_FROM/TO tokenizado si hay filterToId)
   */
  isDate?: boolean;
  className?: string;
  title?: string | undefined;
}

/**
 * Componente genérico para renderizar la etiqueta de un filtro (label + required mark).
 * Pensado para usarse desde los renderers o desde el componente contenedor de filtros.
 */
export const FilterLabel: React.FC<IFilterLabelProps> = ({
  state,
  labelOverride,
  isDate,
  className,
  title,
}) => {
  const text = React.useMemo(() => {
    const base = labelOverride ?? state?.title ?? '';
    if (isDate && state?.filterToId && !state.hideToFilter) {
      // usa el label de "from" tokenizado (consistente con el resto del proyecto)
      try {
        return formatMessage(window.LBL_DATE_FILTER_FROM_TXT, { TOK1: base });
      } catch {
        return base;
      }
    }
    return base;
  }, [labelOverride, state, isDate]);

  const showRequiredBefore = !!(
    state?.required && window.SHOW_REQUIRED_POSITION !== '0'
  );
  const showRequiredAfter = !!(
    state?.required && window.SHOW_REQUIRED_POSITION === '0'
  );

  return (
    <Box
      className={`filterLabel ${className ?? ''}`}
      title={title}
      sx={{ display: 'flex', flexDirection: 'row' }}
    >
      {showRequiredBefore && <RequiredMark isRequired sx={{ zIndex: 1 }} />}
      <Box as="span">{text}</Box>
      {showRequiredAfter && <RequiredMark isRequired sx={{ zIndex: 1 }} />}
    </Box>
  );
};

export default FilterLabel;
